import { useCallback, useEffect, useRef, useState } from 'react'
import { Icon } from '../../../components/icons/Icon'
import { InlineAlert } from '../../../components/InlineAlert'
import { FocusTrapDialog } from '../../../components/FocusTrapDialog'
import { MakerCheckerBar } from './MakerCheckerBar'
import { FixturesResultsList, type FixtureResultRow } from './FixturesResultsList'
import { DecisionGraphCanvas, type CanvasEdge, type CanvasNode } from './DecisionGraphCanvas'
import { DecisionTableGrid } from './DecisionTableGrid'
import { JudgmentGateBuilder } from './JudgmentGateBuilder'
import { usePersistentState } from '../../../lib/usePersistentState'
import {
  approveEligibilityModel,
  createDecision,
  createEligibilityModel,
  createTestFixture,
  getEligibilityModel,
  listDecisionRules,
  listDecisions,
  publishEligibilityModel,
  runEligibilityFixtures,
  submitEligibilityModelForReview,
} from '../../../api/cartridgeStudio'
import { ApiError } from '../../../api/problem'
import type { DecisionKind, DecisionRuleView, DecisionView, EligibilityModelView, FixtureRunSummaryView } from '../../../types/cartridgeStudio'

export interface EligibilityTabProps {
  programId: string
}

const HIT_POLICIES = ['UNIQUE', 'FIRST', 'PRIORITY', 'ANY', 'COLLECT:SUM', 'COLLECT:MIN', 'COLLECT:MAX', 'COLLECT:COUNT']

/**
 * Design doc §4.2 — author-from-scratch Eligibility flow (DoR finding #4: the live demo seeds zero
 * EligibilityModel content). `localStorage` session-persistence (design doc §4.2, no new endpoint)
 * survives a refresh via `GET /eligibility-models/{id}`, which already supports resuming exactly
 * this id.
 */
export function EligibilityTab({ programId }: EligibilityTabProps) {
  const [modelIdByProgram, setModelIdByProgram] = usePersistentState<Record<string, string>>(
    'iw.cartridgeStudio.eligibilityModelByProgram',
    {},
  )
  const persistedModelId = modelIdByProgram[programId] ?? null

  const [model, setModel] = useState<EligibilityModelView | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)

  const [decisions, setDecisions] = useState<DecisionView[]>([])
  const [rulesByDecision, setRulesByDecision] = useState<Record<string, DecisionRuleView[]>>({})
  const [selectedDecisionKey, setSelectedDecisionKey] = useState<string | null>(null)
  const [addDecisionOpen, setAddDecisionOpen] = useState(false)

  const [fixtureResults, setFixtureResults] = useState<FixtureRunSummaryView | null>(null)
  const [fixturesRunning, setFixturesRunning] = useState(false)
  const [mcBusy, setMcBusy] = useState(false)
  const [mcError, setMcError] = useState<string | null>(null)

  useEffect(() => {
    if (!persistedModelId) {
      setModel(null)
      return
    }
    // See ProgramWorkspaceScreen's effect for why the `controller.signal.aborted` guard is needed
    // (React 18 StrictMode double-invoke race).
    const controller = new AbortController()
    setLoadError(null)
    getEligibilityModel(persistedModelId, controller.signal)
      .then((view) => {
        if (controller.signal.aborted) return
        setModel(view)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        if (err instanceof ApiError) setLoadError(err.problem.detail ?? err.message)
        else setLoadError('Could not resume this session\'s EligibilityModel.')
      })
    return () => controller.abort()
  }, [persistedModelId])

  const refreshDecisions = useCallback(async () => {
    if (!model) return
    const list = await listDecisions(model.id)
    setDecisions(list)
    const rulesEntries = await Promise.all(
      list.filter((d) => d.kind === 'table').map(async (d) => [d.id, await listDecisionRules(d.id)] as const),
    )
    setRulesByDecision(Object.fromEntries(rulesEntries))
    if (!selectedDecisionKey && list.length > 0) setSelectedDecisionKey(list[0]!.key)
  }, [model, selectedDecisionKey])

  useEffect(() => {
    void refreshDecisions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model?.id])

  const runFixtures = useCallback(async () => {
    if (!model) return
    setFixturesRunning(true)
    try {
      const summary = await runEligibilityFixtures(model.id)
      setFixtureResults(summary)
    } catch {
      // A fixture-run itself failing (vs. individual fixtures failing) is rare and non-actionable
      // here beyond leaving the prior result visible — the publish path below still surfaces its
      // own 422 with the real violation list, which is the AC's actual failure-path requirement.
    } finally {
      setFixturesRunning(false)
    }
  }, [model])

  // AC6 — re-run automatically after every Decision/Rule/Gate/Fixture mutation, not a manual step.
  useEffect(() => {
    if (model && decisions.length > 0) void runFixtures()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model?.id, decisions.length, JSON.stringify(rulesByDecision)])

  function violationsText(err: unknown, fallback: string): string {
    if (err instanceof ApiError) {
      const violations = err.problem.violations
      if (violations && violations.length > 0) return violations.map((v) => v.message ?? v.code).join('; ')
      return err.problem.detail ?? err.message
    }
    return fallback
  }

  async function runTransition(fn: (id: string) => Promise<EligibilityModelView>) {
    if (!model) return
    setMcBusy(true)
    setMcError(null)
    try {
      const updated = await fn(model.id)
      setModel(updated)
    } catch (err) {
      setMcError(violationsText(err, 'Could not update this EligibilityModel.'))
    } finally {
      setMcBusy(false)
    }
  }

  if (!persistedModelId || loadError) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--iw-space-3)', padding: 'var(--iw-space-7)', textAlign: 'center' }}>
        <Icon name="check-circle" size={32} style={{ color: 'var(--iw-text-subtle)' }} />
        <p style={{ margin: 0, fontWeight: 'var(--iw-weight-medium)' as never, color: 'var(--iw-text)' }}>
          No EligibilityModel authored this session yet
        </p>
        <p style={{ margin: 0, fontSize: 'var(--iw-size-sm)', color: 'var(--iw-text-muted)', maxWidth: 480 }}>
          There is no list-by-program route for EligibilityModel yet, so this tab is author-from-scratch:
          create a new draft model, then add Decisions, rules, a judgment gate, and fixtures live in this
          session.
        </p>
        {loadError ? <InlineAlert severity="error">{loadError}</InlineAlert> : null}
        <button type="button" className="iw-btn iw-btn--primary" onClick={() => setCreateOpen(true)}>
          Create EligibilityModel
        </button>
        {createOpen ? (
          <NewEligibilityModelDialog
            programId={programId}
            onClose={() => setCreateOpen(false)}
            onCreated={(created) => {
              setCreateOpen(false)
              setModelIdByProgram((prev) => ({ ...prev, [programId]: created.id }))
              setModel(created)
              setLoadError(null)
            }}
          />
        ) : null}
      </div>
    )
  }

  if (!model) {
    return <div style={{ color: 'var(--iw-text-muted)' }}>Loading…</div>
  }

  const nodes: CanvasNode[] = decisions.map((d) => ({
    key: d.key,
    label: d.key,
    variant: d.kind,
    subtitle: d.citationRef ?? undefined,
  }))
  const edges: CanvasEdge[] = inferEdges(decisions, rulesByDecision)
  const selectedDecision = decisions.find((d) => d.key === selectedDecisionKey) ?? null

  const resultRows: FixtureResultRow[] =
    fixtureResults?.results.map((r) => ({
      fixtureId: r.fixtureId,
      label: r.fixtureId.slice(0, 8),
      expected: r.expectedVerdict,
      actual: r.actualVerdict,
      passed: r.passed,
    })) ?? []

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
      <MakerCheckerBar
        artifactLabel={`EligibilityModel v${model.version}`}
        variant="lifecycle"
        status={model.status}
        busy={mcBusy}
        errorMessage={mcError}
        onSubmitForReview={() => void runTransition((id) => submitEligibilityModelForReview(id, { comment: null }))}
        onApprove={() => void runTransition((id) => approveEligibilityModel(id, { comment: null }))}
        onPublish={() => void runTransition((id) => publishEligibilityModel(id, { comment: null }))}
        onNewVersion={() => {
          setModelIdByProgram((prev) => {
            const next = { ...prev }
            delete next[programId]
            return next
          })
          setModel(null)
        }}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 'var(--iw-weight-semibold)' as never }}>Decision Requirements Diagram</span>
        <button type="button" className="iw-btn iw-btn--sm" onClick={() => setAddDecisionOpen(true)}>
          <Icon name="plus" size={13} />
          Add Decision
        </button>
      </div>

      <DecisionGraphCanvas
        ariaLabel="Eligibility decision requirements diagram"
        nodes={nodes}
        edges={edges}
        selectedKey={selectedDecisionKey}
        onSelect={setSelectedDecisionKey}
        emptyMessage="No Decisions yet — add the first one."
        caption="Edges inferred from referenced decision keys — a missed inference never hides a Decision; see the list view."
      />

      {selectedDecision ? (
        <div style={{ border: '1px solid var(--iw-border)', borderRadius: 'var(--iw-radius)', padding: 'var(--iw-space-3)' }}>
          <p style={{ margin: '0 0 var(--iw-space-2)', fontWeight: 'var(--iw-weight-medium)' as never }}>
            {selectedDecision.key} <span style={{ color: 'var(--iw-text-muted)', fontWeight: 'var(--iw-weight-regular)' as never }}>({selectedDecision.kind})</span>
          </p>
          {selectedDecision.kind === 'table' ? (
            <DecisionTableGrid decision={selectedDecision} onRuleAdded={() => void refreshDecisions()} />
          ) : selectedDecision.kind === 'gate' ? (
            <JudgmentGateBuilder decision={selectedDecision} />
          ) : (
            <p style={{ margin: 0, color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)' }}>
              {selectedDecision.citationRef ? `Citation: ${selectedDecision.citationRef}` : 'No citation recorded.'} — no rule/gate editor
              for this kind yet.
            </p>
          )}
        </div>
      ) : null}

      <FixturesTester modelId={model.id} modelVersion={model.version} onFixtureAdded={() => void runFixtures()} />

      <FixturesResultsList
        mode="gates-publish"
        results={fixtureResults ? resultRows : null}
        allPassed={fixtureResults?.allPassed ?? null}
        onRun={() => void runFixtures()}
        running={fixturesRunning}
      />

      {addDecisionOpen ? (
        <NewDecisionDialog
          modelId={model.id}
          onClose={() => setAddDecisionOpen(false)}
          onCreated={() => {
            setAddDecisionOpen(false)
            void refreshDecisions()
          }}
        />
      ) : null}
    </div>
  )
}

/** Design doc §4.3's disclosed, best-effort client-side heuristic — the API carries no edge list. */
function inferEdges(decisions: DecisionView[], rulesByDecision: Record<string, DecisionRuleView[]>): CanvasEdge[] {
  const edges: CanvasEdge[] = []
  for (const decision of decisions) {
    if (decision.kind !== 'table') continue
    const rules = rulesByDecision[decision.id] ?? []
    const haystack = JSON.stringify(rules.map((r) => [r.conditions, r.conclusions]))
    for (const other of decisions) {
      if (other.key === decision.key) continue
      if (haystack.includes(other.key)) edges.push({ from: other.key, to: decision.key })
    }
  }
  return edges
}

function NewEligibilityModelDialog({
  programId,
  onClose,
  onCreated,
}: {
  programId: string
  onClose: () => void
  onCreated: (model: EligibilityModelView) => void
}) {
  const [version, setVersion] = useState('1')
  const [validFrom, setValidFrom] = useState(() => new Date().toISOString().slice(0, 10))
  const [validTo, setValidTo] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const versionRef = useRef<HTMLInputElement>(null)

  const parsedVersion = Number(version)
  const isValid = Number.isInteger(parsedVersion) && parsedVersion >= 1 && validFrom !== ''

  async function handleSubmit() {
    if (!isValid || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const created = await createEligibilityModel({ programId, version: parsedVersion, validFrom, validTo: validTo || null })
      onCreated(created)
    } catch (err) {
      setError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not create this EligibilityModel.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <FocusTrapDialog
      titleId="new-eligibility-model-title"
      title="Create EligibilityModel"
      onClose={onClose}
      initialFocusRef={versionRef}
      footer={
        <>
          <button type="button" className="iw-btn" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="button" className="iw-btn iw-btn--primary" onClick={() => void handleSubmit()} disabled={!isValid || submitting}>
            {submitting ? 'Creating…' : 'Create'}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void handleSubmit()
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}
      >
        {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}
        <div className="iw-field">
          <label className="iw-field-label" htmlFor="new-elig-version">
            Version
          </label>
          <input id="new-elig-version" ref={versionRef} className="iw-input" type="number" min={1} value={version} onChange={(e) => setVersion(e.target.value)} />
        </div>
        <div className="iw-field">
          <label className="iw-field-label" htmlFor="new-elig-valid-from">
            Valid from
          </label>
          <input id="new-elig-valid-from" className="iw-input" type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
        </div>
        <div className="iw-field">
          <label className="iw-field-label" htmlFor="new-elig-valid-to">
            Valid to (optional)
          </label>
          <input id="new-elig-valid-to" className="iw-input" type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} />
        </div>
      </form>
    </FocusTrapDialog>
  )
}

function NewDecisionDialog({ modelId, onClose, onCreated }: { modelId: string; onClose: () => void; onCreated: () => void }) {
  const [key, setKey] = useState('')
  const [kind, setKind] = useState<DecisionKind>('table')
  const [hitPolicy, setHitPolicy] = useState('UNIQUE')
  const [citationRef, setCitationRef] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const keyRef = useRef<HTMLInputElement>(null)

  const isValid = key.trim() !== '' && (kind !== 'table' || hitPolicy !== '')

  async function handleSubmit() {
    if (!isValid || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await createDecision(modelId, {
        key: key.trim(),
        kind,
        hitPolicy: kind === 'table' ? hitPolicy : 'ANY',
        citationRef: citationRef.trim() || null,
      })
      onCreated()
    } catch (err) {
      const violations = err instanceof ApiError ? err.problem.violations : undefined
      setError(
        violations && violations.length > 0
          ? violations.map((v) => v.message ?? v.code).join('; ')
          : err instanceof ApiError
            ? (err.problem.detail ?? err.message)
            : 'Could not add this Decision.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <FocusTrapDialog
      titleId="new-decision-title"
      title="Add Decision"
      onClose={onClose}
      initialFocusRef={keyRef}
      footer={
        <>
          <button type="button" className="iw-btn" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="button" className="iw-btn iw-btn--primary" onClick={() => void handleSubmit()} disabled={!isValid || submitting}>
            {submitting ? 'Adding…' : 'Add Decision'}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void handleSubmit()
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}
      >
        {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}
        <div className="iw-field">
          <label className="iw-field-label" htmlFor="new-decision-key">
            Key
          </label>
          <input id="new-decision-key" ref={keyRef} className="iw-input" value={key} onChange={(e) => setKey(e.target.value)} />
        </div>
        <div className="iw-field">
          <label className="iw-field-label" htmlFor="new-decision-kind">
            Kind
          </label>
          <select id="new-decision-kind" className="iw-input" value={kind} onChange={(e) => setKind(e.target.value as DecisionKind)}>
            <option value="table">table</option>
            <option value="gate">gate</option>
            <option value="aggregate">aggregate</option>
          </select>
        </div>
        {kind === 'table' ? (
          <div className="iw-field">
            <label className="iw-field-label" htmlFor="new-decision-hitpolicy">
              Hit policy
            </label>
            <select id="new-decision-hitpolicy" className="iw-input" value={hitPolicy} onChange={(e) => setHitPolicy(e.target.value)}>
              {HIT_POLICIES.map((hp) => (
                <option key={hp} value={hp}>
                  {hp}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <div className="iw-field">
          <label className="iw-field-label" htmlFor="new-decision-citation">
            Citation ref (optional)
          </label>
          <input id="new-decision-citation" className="iw-input" value={citationRef} onChange={(e) => setCitationRef(e.target.value)} placeholder="e.g. O.C.G.A. §48-7-40.26(b)(1)" />
        </div>
      </form>
    </FocusTrapDialog>
  )
}

function FixturesTester({ modelId, modelVersion, onFixtureAdded }: { modelId: string; modelVersion: number; onFixtureAdded: () => void }) {
  const [subjectId, setSubjectId] = useState('subject-1')
  const [facts, setFacts] = useState('{}')
  const [expectedVerdict, setExpectedVerdict] = useState('pass')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  let parsedFacts: unknown = null
  let jsonError = false
  try {
    parsedFacts = JSON.parse(facts)
  } catch {
    jsonError = true
  }
  const isValid = subjectId.trim() !== '' && !jsonError

  async function handleAdd() {
    if (!isValid || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await createTestFixture(modelId, {
        modelVersion,
        inputs: { subjects: [{ subjectId: subjectId.trim(), facts: parsedFacts }] },
        expectedVerdict: { verdicts: { subjects: { [subjectId.trim()]: expectedVerdict } } },
      })
      onFixtureAdded()
    } catch (err) {
      const violations = err instanceof ApiError ? err.problem.violations : undefined
      setError(
        violations && violations.length > 0
          ? violations.map((v) => v.message ?? v.code).join('; ')
          : err instanceof ApiError
            ? (err.problem.detail ?? err.message)
            : 'Could not add this fixture.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{ border: '1px solid var(--iw-border)', borderRadius: 'var(--iw-radius)', padding: 'var(--iw-space-3)', display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)' }}>
      <span style={{ fontWeight: 'var(--iw-weight-medium)' as never }}>Author a fixture</span>
      {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}
      <div className="iw-field">
        <label className="iw-field-label" htmlFor="fixture-subject-id">
          Subject id
        </label>
        <input id="fixture-subject-id" className="iw-input" value={subjectId} onChange={(e) => setSubjectId(e.target.value)} />
      </div>
      <div className="iw-field">
        <label className="iw-field-label" htmlFor="fixture-facts">
          Facts (JSON)
        </label>
        <textarea id="fixture-facts" className="iw-input" rows={2} style={{ fontFamily: 'var(--iw-font-mono)' }} value={facts} onChange={(e) => setFacts(e.target.value)} />
      </div>
      <div className="iw-field">
        <label className="iw-field-label" htmlFor="fixture-expected-verdict">
          Expected verdict
        </label>
        <select id="fixture-expected-verdict" className="iw-input" value={expectedVerdict} onChange={(e) => setExpectedVerdict(e.target.value)}>
          <option value="pass">pass</option>
          <option value="fail">fail</option>
          <option value="needs_review">needs_review</option>
        </select>
        <span style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
          Every judgment-gate Decision always evaluates <code>needs_review</code> in a fixture run (no per-filing
          context exists here) — author gate-bearing models&apos; fixtures with <code>needs_review</code>.
        </span>
      </div>
      {jsonError ? <span style={{ color: 'var(--iw-danger)', fontSize: 'var(--iw-size-xs)' }}>Facts must be valid JSON.</span> : null}
      <button type="button" className="iw-btn iw-btn--sm" onClick={() => void handleAdd()} disabled={!isValid || submitting}>
        <Icon name="plus" size={13} />
        Add fixture
      </button>
    </div>
  )
}
